import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Component, inject, OnInit, signal } from '@angular/core';

import { IonButton, IonText } from '@ionic/angular/standalone';

import { ANALYTICS_PRIVACY_POLICY_LINK, ConsentDialogManager, ConsentRepository } from '@guardfinder/analytics';

@UntilDestroy()
@Component({
    selector: 'lib-analytics-consent-modal',
    imports: [IonText, IonButton],
    templateUrl: './analytics-consent-modal.component.html',
    styleUrl: './analytics-consent-modal.component.scss'
})
/**
 * Component that presents the analytics consent dialog to users.
 * This modal allows users to accept or reject analytics tracking,
 * ensuring GDPR compliance by obtaining explicit consent before
 * collecting non-essential analytics data.
 */
export class AnalyticsConsentModalComponent implements OnInit {
    /**
     * URL to the privacy policy that users can visit for more information.
     * Injected from the ANALYTICS_PRIVACY_POLICY_LINK token.
     */
    protected readonly privacyPolicyUrl = inject(ANALYTICS_PRIVACY_POLICY_LINK);

    /**
     * Service that manages the visibility state of the consent dialog.
     * Used to show/hide the modal and track its visibility state.
     */
    protected readonly consentDialogManager = inject(ConsentDialogManager);

    /**
     * Signal that tracks whether the consent modal should be shown.
     * Updated based on the ConsentDialogManager's state.
     */
    protected readonly isConsentShown = signal<boolean>(false);

    /**
     * Repository service for storing and retrieving user consent preferences.
     * Used to save the user's consent decision.
     */
    private readonly consentStorage = inject(ConsentRepository);

    /**
     * Initializes the component by subscribing to the consent dialog visibility state.
     * Updates the isConsentShown signal whenever the visibility state changes.
     */
    ngOnInit(): void {
        this.consentDialogManager
            .getConsent()
            .pipe(untilDestroyed(this))
            .subscribe(value => {
                this.isConsentShown.set(value);
            });
    }

    /**
     * Handles the user accepting analytics consent.
     * Saves the consent state with isGiven=true and hides the consent dialog.
     *
     * @returns A promise that resolves when the consent is saved and the dialog is hidden
     */
    async onAccept(): Promise<void> {
        await this.consentStorage.setConsent({
            hasBeenPrompted: true,
            isGiven: true,
            version: '1.0.0'
        });
        await this.consentDialogManager.hideConsent();
    }

    /**
     * Handles the user rejecting analytics consent.
     * Saves the consent state with isGiven=false and hides the consent dialog.
     *
     * @returns A promise that resolves when the consent is saved and the dialog is hidden
     */
    async onReject(): Promise<void> {
        await this.consentStorage.setConsent({
            hasBeenPrompted: true,
            isGiven: false,
            version: '1.0.0'
        });
        await this.consentDialogManager.hideConsent();
    }
}
